import { asMe } from "./auth.ts";
import { readSharedGame, type SharedGame } from "./share.ts";
import { trackId } from "./track.ts";

/**
 * Where a shared game is kept.
 *
 * Charts and analysis only. The music stays wherever the creator put it and travels as a URL,
 * so nothing sent here is a copy of anybody's record.
 */

const FALLBACK = "http://localhost:8788";

export function resolveGamesOrigin(): string {
  const configured = import.meta.env.VITE_GAMES_ORIGIN;
  return typeof configured === "string" && configured.length > 0 ? configured : FALLBACK;
}

/** The id in the address, if this window was opened to play somebody's game. */
export function sharedGameId(): string | null {
  const id = new URLSearchParams(location.search).get("g");
  return id !== null && /^[A-Z0-9]{12}$/.test(id) ? id : null;
}

export function shareUrl(id: string): string {
  return `${location.origin}${location.pathname}?g=${id}`;
}

export const GAME_MODES = ["lanes", "armada"] as const;
// Reachable by address only. Nothing joins the picker before somebody has said it is fun.
const GAME_ROUTE_MODES = [...GAME_MODES, "beatstack", "drive"] as const;

/**
 * Which of the games a chart is being read as.
 *
 * The player's choice and not the creator's, for the same reason the play styles are: a chart
 * is a list of weighted moments and says nothing about how it should be played, so one of them
 * drives the highway and the ring without being written twice. Making it a property of the
 * published game would have fragmented the links; making it a property of the player means a
 * shared chart can gain another way to play without being republished.
 */
export type GameMode = (typeof GAME_ROUTE_MODES)[number];

export function isGameMode(value: unknown): value is GameMode {
  return typeof value === "string" && (GAME_ROUTE_MODES as readonly string[]).includes(value);
}

export const GAME_NAMES: Record<GameMode, string> = {
  lanes: "Lanes",
  armada: "Armada",
  beatstack: "Beatstack",
  drive: "Drive",
};

/**
 * What to do, in one line, before anybody has to work it out.
 *
 * Not decoration and not marketing. The flow literature puts knowing what to do first, ahead of
 * any question of how hard a thing is, and the first person to try the ring said they were not
 * sure how it worked - which is the whole game failing before a note is played. One sentence is
 * a cheap fix for the most expensive possible problem.
 */
export const GAME_HOWTO: Record<GameMode, string> = {
  lanes: "Three columns. Tap the one a note lands in.",
  armada: "Raiders march down on the beat. Hold a column to fire on every beat.",
  beatstack: "Drag the block. Every beat locks it onto the tower.",
  drive: "Drag to steer. Hold the line the song is taking.",
};

/**
 * Which game a window was opened to play.
 *
 * Read from the address so a link can name one, and remembered otherwise, because whichever a
 * person played last is overwhelmingly the one they mean to play next.
 */
export function openedMode(): GameMode {
  const asked = new URLSearchParams(location.search).get("m");
  if (isGameMode(asked)) return asked;
  try {
    const kept = localStorage.getItem("dodobop.mode");
    if (isGameMode(kept)) return kept;
  } catch {
    // Private mode. The highway is the right default anyway.
  }
  return "lanes";
}

export function rememberMode(mode: GameMode): void {
  try {
    localStorage.setItem("dodobop.mode", mode);
  } catch {
    // Private mode. Choosing again next time is no hardship.
  }
}

/**
 * The key a board, a personal best and a ghost are all filed under.
 *
 * Aiming around a ring and reacting on a highway are not the same skill, so one list would only
 * rank whichever is the easier of the two. The highway keeps the bare level it has always sent,
 * so every board written before there were two of them stays exactly where it is.
 */
export function boardKey(mode: GameMode, level: string): string {
  return mode === "lanes" ? level : `${mode}:${level}`;
}

/**
 * What went wrong, in words, rather than the store's own JSON.
 *
 * Nothing here is the creator's fault and none of it is anything they can act on, so it says
 * so. A raw `{"error":"version"}` on screen is the app blaming somebody for its own upgrade.
 */
function explainPublishFailure(status: number, body: string): string {
  if (status === 413 || body.includes("too large")) {
    return "That track is too long to share. Try one under about fifteen minutes.";
  }
  if (body.includes("version")) {
    return "The place these are kept has not caught up with this app yet. Try again shortly.";
  }
  if (status === 403) {
    return "This copy of the app is not allowed to publish games.";
  }
  return "That could not be shared just now. Try again in a moment.";
}

export async function publishGame(game: SharedGame): Promise<string> {
  let response: Response;
  try {
    response = await fetch(`${resolveGamesOrigin()}/games`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(game),
    });
  } catch {
    /*
     * A dropped answer is not a failed write.
     *
     * The chart is a large POST and the reply is small, so the connection is most likely to
     * break after the store has already filed it. Retrying blindly would leave two copies of
     * the same song, so ask the index instead: if the game is there, it was ours.
     */
    const landed = await findGameForSong(trackId(game.audio), chartVersionOf(game));
    if (landed !== null) return landed;
    throw new Error("The connection dropped before the game was saved. Try that one again.");
  }
  if (!response.ok) {
    throw new Error(explainPublishFailure(response.status, await response.text().catch(() => "")));
  }
  const body = (await response.json()) as { id?: unknown };
  if (typeof body.id !== "string") throw new Error("The game store answered with no id.");
  return body.id;
}

function chartVersionOf(game: SharedGame): number {
  const written = game.provenance?.chartVersion;
  return typeof written === "number" && Number.isInteger(written) && written > 0 ? written : 0;
}

/** Nothing that arrives here is trusted; it is somebody else's link. */
export async function fetchGame(id: string): Promise<SharedGame> {
  // Signed in where possible, because the words of a song are only returned to whoever made it.
  const response = await fetch(`${resolveGamesOrigin()}/games/${id}`, { headers: asMe() });
  if (response.status === 410) throw new Error("That game has been taken down.");
  if (!response.ok) throw new Error("That game could not be found.");
  const game = readSharedGame(await response.json());
  if (game === null) throw new Error("That game is not one this version can play.");
  return game;
}

/** A game already made from this track, if somebody got there first. */
export async function findGameForSong(songId: string, chartVersion = 0): Promise<string | null> {
  try {
    const response = await fetch(`${resolveGamesOrigin()}/games/for/${songId}?c=${chartVersion}`);
    if (!response.ok) return null;
    const body = (await response.json()) as { id?: unknown };
    return typeof body.id === "string" && /^[A-Z0-9]{12}$/.test(body.id) ? body.id : null;
  } catch {
    // A lookup that cannot be reached means making a second copy, which beats stopping.
    return null;
  }
}

export interface Score {
  readonly name: string;
  readonly score: number;
  readonly accuracy: number;
  readonly grade: string;
  /** Which chart it was played against, when the run said. */
  readonly hash?: string;
}

/**
 * Who has played this one well.
 *
 * Read as data and rendered as text. Nothing here builds markup from it, which matters because
 * every one of these names was typed by a stranger.
 */
function readScores(value: unknown): Score[] {
  if (!Array.isArray(value)) return [];
  const out: Score[] = [];
  for (const entry of value) {
    if (typeof entry !== "object" || entry === null) continue;
    const row = entry as Record<string, unknown>;
    if (typeof row.score !== "number" || !Number.isFinite(row.score)) continue;
    out.push({
      name: typeof row.name === "string" ? row.name.slice(0, 24) : "Someone",
      score: Math.max(0, Math.floor(row.score)),
      accuracy: typeof row.accuracy === "number" ? Math.min(1, Math.max(0, row.accuracy)) : 0,
      grade: typeof row.grade === "string" ? row.grade.slice(0, 2) : "-",
      ...(typeof row.hash === "string" && /^[0-9a-f]{8,32}$/.test(row.hash)
        ? { hash: row.hash }
        : {}),
    });
  }
  return out;
}

export async function fetchScores(id: string, level = "normal"): Promise<Score[]> {
  try {
    const response = await fetch(`${resolveGamesOrigin()}/games/${id}/scores?d=${level}`);
    if (!response.ok) return [];
    const body = (await response.json()) as { scores?: unknown };
    return readScores(body.scores);
  } catch {
    // A board that cannot be reached is not a reason to refuse somebody a game.
    return [];
  }
}

export async function postScore(
  id: string,
  entry: { name: string; score: number; accuracy: number; grade: string; hash?: string },
  level = "normal",
): Promise<Score[]> {
  const response = await fetch(`${resolveGamesOrigin()}/games/${id}/scores?d=${level}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(entry),
  });
  if (!response.ok) throw new Error("That score could not be posted.");
  const body = (await response.json()) as { scores?: unknown };
  return readScores(body.scores);
}

export type AudioProbe = "ok" | "not-audio" | "slow";

/**
 * Does this address actually give a browser something it can play?
 *
 * Asked by trying it, because there is no other way. The headers cannot be read across origins
 * and guessing from the path is wrong as often as it is right.
 *
 * A media element pointed at a web page fires NO error event whatever. It sits at readyState
 * zero with a duration of NaN until something else gives up, which is why an unplayable link
 * used to produce a share that did nothing at all rather than a message. What it does do is
 * settle at NETWORK_NO_SOURCE, so that is what is watched for.
 */
export function probeAudioUrl(url: string, timeoutMs = 9000): Promise<AudioProbe> {
  return new Promise((resolve) => {
    const el = document.createElement("audio");
    el.preload = "metadata";
    el.muted = true;

    let settled = false;
    const finish = (result: AudioProbe): void => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(giveUp);
      el.removeAttribute("src");
      el.load();
      resolve(result);
    };

    el.addEventListener("loadedmetadata", () => finish("ok"), { once: true });
    el.addEventListener("error", () => finish("not-audio"), { once: true });
    const poll = setInterval(() => {
      if (el.networkState === el.NETWORK_NO_SOURCE) finish("not-audio");
    }, 150);
    const giveUp = setTimeout(() => finish("slow"), timeoutMs);

    el.src = url;
    el.load();
  });
}
